// src/pages/tenant/TenantGeneralSettingsPage.tsx
// Tenant general settings: info, visibility, localization, feature toggles.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useTenantTenant, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VISIBILITY_OPTIONS = [
  { value: 'PUBLIC', label: 'Public' },
  { value: 'AUTHENTICATED', label: 'Authenticated' },
  { value: 'MEMBERS_ONLY', label: 'Members Only' },
  { value: 'INVITE_ONLY', label: 'Invite Only' },
] as const;

const TIMEZONE_OPTIONS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Pacific/Auckland',
] as const;

const LOCALE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Russian' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'ar', label: 'Arabic' },
] as const;

const REGION_OPTIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'EU (Ireland)' },
  { value: 'eu-central-1', label: 'EU (Frankfurt)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'sa-east-1', label: 'South America (Sao Paulo)' },
] as const;

// ---------------------------------------------------------------------------
// Toggle Switch component
// ---------------------------------------------------------------------------

function ToggleSwitch({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
          checked ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
      <div className="min-w-0">
        <span className="text-sm font-medium text-foreground">{label}</span>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * TenantGeneralSettingsPage - edit basic tenant info, visibility/privacy,
 * localization, and feature toggles.
 * Route: /tenants/:id/settings
 */
export const TenantGeneralSettingsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTenant();
  const crud = useTenantTenant();
  const { t } = useI18n();

  // Latest settings loaded from the server (used as merge base on save)
  const [loadedSettings, setLoadedSettings] = useState<any>({});

  // Per-card save feedback
  const [generalSaved, setGeneralSaved] = useState(false);
  const [visibilitySaved, setVisibilitySaved] = useState(false);
  const [localizationSaved, setLocalizationSaved] = useState(false);
  const [featuresSaved, setFeaturesSaved] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  // Per-card submitting state
  const [generalSubmitting, setGeneralSubmitting] = useState(false);
  const [visibilitySubmitting, setVisibilitySubmitting] = useState(false);
  const [localizationSubmitting, setLocalizationSubmitting] = useState(false);
  const [featuresSubmitting, setFeaturesSubmitting] = useState(false);

  // General Information form
  const [generalForm, setGeneralForm] = useState({
    name: '',
    slug: '',
    description: '',
    region: '',
  });

  // Visibility & Privacy form
  const [visibilityForm, setVisibilityForm] = useState({
    visibility: 'PUBLIC',
    show_in_search: true,
    show_in_directory: true,
    show_member_count: true,
    show_member_list: false,
    allow_join_requests: true,
  });

  // Localization form
  const [localizationForm, setLocalizationForm] = useState({
    default_timezone: 'UTC',
    default_locale: 'en',
    data_retention_days: 365,
  });

  // Feature Toggles form
  const [featuresForm, setFeaturesForm] = useState({
    enable_api_access: false,
    enable_webhooks: false,
    enable_integrations: false,
    enable_audit_logging: false,
  });

  // Load tenant and settings on mount
  useEffect(() => {
    if (id) {
      crud.GetTenant(id).catch(() => {});
      store.GetTenantSettings(id).then((res: any) => {
        setLoadedSettings(res?.settings ?? {});
      }).catch(() => {});
    }
    return () => {
      crud.clearCurrent();
    };
  }, [id]);

  // Initialize forms from store data
  useEffect(() => {
    if (crud.currentItem) {
      const item = crud.currentItem as any;
      setGeneralForm({
        name: item.name ?? '',
        slug: item.slug ?? '',
        description: item.description ?? '',
        region: item.region ?? '',
      });
    }
  }, [crud.currentItem]);

  useEffect(() => {
    {
      const settings = loadedSettings ?? {};
      setVisibilityForm({
        visibility: (settings.visibility ?? 'TENANT_VISIBILITY_PUBLIC').replace('TENANT_VISIBILITY_', ''),
        show_in_search: settings.show_in_search ?? true,
        show_in_directory: settings.show_in_directory ?? true,
        show_member_count: settings.show_member_count ?? true,
        show_member_list: settings.show_member_list ?? false,
        allow_join_requests: settings.allow_join_requests ?? true,
      });
      setLocalizationForm({
        default_timezone: settings.default_timezone ?? 'UTC',
        default_locale: settings.default_locale ?? 'en',
        data_retention_days: settings.data_retention_days ?? 365,
      });
      setFeaturesForm({
        enable_api_access: settings.enable_api_access ?? false,
        enable_webhooks: settings.enable_webhooks ?? false,
        enable_integrations: settings.enable_integrations ?? false,
        enable_audit_logging: settings.enable_audit_logging ?? false,
      });
    }
  }, [loadedSettings]);

  const tenantName = (crud.currentItem as any)?.name || t('tenant.title');

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const clearFeedback = () => {
    setSaveError(null);
    setGeneralSaved(false);
    setVisibilitySaved(false);
    setLocalizationSaved(false);
    setFeaturesSaved(false);
  };

  const handleSaveGeneral = async () => {
    if (!id) return;
    clearFeedback();
    setGeneralSubmitting(true);
    try {
      await crud.UpdateTenant(id, {
        tenant: { ...(crud.currentItem as any), ...generalForm },
      });
      setGeneralSaved(true);
      setTimeout(() => setGeneralSaved(false), 3000);
    } catch (err) {
      setSaveError(err);
    } finally {
      setGeneralSubmitting(false);
    }
  };

  const handleSaveVisibility = async () => {
    if (!id) return;
    clearFeedback();
    setVisibilitySubmitting(true);
    try {
      await store.UpdateTenantSettings(id, {
        settings: {
          ...loadedSettings,
          ...visibilityForm,
          visibility: `TENANT_VISIBILITY_${visibilityForm.visibility}` as any,
        },
      });
      setVisibilitySaved(true);
      setTimeout(() => setVisibilitySaved(false), 3000);
    } catch (err) {
      setSaveError(err);
    } finally {
      setVisibilitySubmitting(false);
    }
  };

  const handleSaveLocalization = async () => {
    if (!id) return;
    clearFeedback();
    setLocalizationSubmitting(true);
    try {
      await store.UpdateTenantSettings(id, {
        settings: { ...loadedSettings, ...localizationForm },
      });
      setLocalizationSaved(true);
      setTimeout(() => setLocalizationSaved(false), 3000);
    } catch (err) {
      setSaveError(err);
    } finally {
      setLocalizationSubmitting(false);
    }
  };

  const handleSaveFeatures = async () => {
    if (!id) return;
    clearFeedback();
    setFeaturesSubmitting(true);
    try {
      await store.UpdateTenantSettings(id, {
        settings: { ...loadedSettings, ...featuresForm },
      });
      setFeaturesSaved(true);
      setTimeout(() => setFeaturesSaved(false), 3000);
    } catch (err) {
      setSaveError(err);
    } finally {
      setFeaturesSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  if ((store.isLoading || crud.isLoadingItem) && !crud.currentItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Breadcrumb & header */}
      <div>
        <nav className="text-sm">
          <Link to="/tenants" className="text-muted-foreground hover:text-foreground">
            {t('tenant.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/tenants/${id}`} className="text-muted-foreground hover:text-foreground">
            {tenantName}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-muted-foreground">{t('tenant.settings')}</span>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('tenant.generalSettings')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('tenant.generalSettings')}</h1>
      </div>

      {/* Global alerts */}
      {saveError ? (
        <ErrorAlert error={saveError} dismissible onDismiss={() => setSaveError(null)} />
      ) : null}

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      {crud.error ? <ErrorAlert error={crud.error} dismissible onDismiss={() => crud.clearError()} /> : null}

      {/* ================================================================= */}
      {/* Card 1: General Information */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('tenant.generalInformation')}</CardTitle>
              <CardDescription>{t('tenant.generalInformationDescription')}</CardDescription>
            </div>
            {generalSaved && (
              <Badge variant="success" size="sm">{t('tenant.saved')}</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label={t('tenant.name')}
            type="text"
            value={generalForm.name}
            onChange={(e) => setGeneralForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder={t('tenant.namePlaceholder')}
            required
          />
          <div>
            <Input
              label={t('tenant.slug')}
              type="text"
              value={generalForm.slug}
              onChange={(e) =>
                setGeneralForm((prev) => ({
                  ...prev,
                  slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                }))
              }
              placeholder="my-organization"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('tenant.slugHint')}: <span className="font-mono">{generalForm.slug || '...'}</span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('tenant.description')}
            </label>
            <textarea
              value={generalForm.description}
              onChange={(e) => setGeneralForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder={t('tenant.descriptionPlaceholder')}
              rows={3}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('tenant.region')}
            </label>
            <Select
              value={generalForm.region}
              onChange={(e) => setGeneralForm((prev) => ({ ...prev, region: e.target.value }))} className="w-full">
              <option value="">{t('tenant.selectRegion')}</option>
              {REGION_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveGeneral} disabled={generalSubmitting}>
            {generalSubmitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>

      {/* ================================================================= */}
      {/* Card 2: Visibility & Privacy */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('tenant.visibilityPrivacy')}</CardTitle>
              <CardDescription>{t('tenant.visibilityPrivacyDescription')}</CardDescription>
            </div>
            {visibilitySaved && (
              <Badge variant="success" size="sm">{t('tenant.saved')}</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('tenant.visibility')}
            </label>
            <Select
              value={visibilityForm.visibility}
              onChange={(e) => setVisibilityForm((prev) => ({ ...prev, visibility: e.target.value })) } className="w-full">
              {VISIBILITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              {visibilityForm.visibility === 'PUBLIC' && t('tenant.visibilityPublicHint')}
              {visibilityForm.visibility === 'AUTHENTICATED' && t('tenant.visibilityAuthenticatedHint')}
              {visibilityForm.visibility === 'MEMBERS_ONLY' && t('tenant.visibilityMembersOnlyHint')}
              {visibilityForm.visibility === 'INVITE_ONLY' && t('tenant.visibilityInviteOnlyHint')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ToggleSwitch
              checked={visibilityForm.show_in_search}
              onChange={(v) => setVisibilityForm((prev) => ({ ...prev, show_in_search: v }))}
              label={t('tenant.showInSearch')}
              description={t('tenant.showInSearchDescription')}
            />
            <ToggleSwitch
              checked={visibilityForm.show_in_directory}
              onChange={(v) => setVisibilityForm((prev) => ({ ...prev, show_in_directory: v }))}
              label={t('tenant.showInDirectory')}
              description={t('tenant.showInDirectoryDescription')}
            />
            <ToggleSwitch
              checked={visibilityForm.show_member_count}
              onChange={(v) => setVisibilityForm((prev) => ({ ...prev, show_member_count: v }))}
              label={t('tenant.showMemberCount')}
              description={t('tenant.showMemberCountDescription')}
            />
            <ToggleSwitch
              checked={visibilityForm.show_member_list}
              onChange={(v) => setVisibilityForm((prev) => ({ ...prev, show_member_list: v }))}
              label={t('tenant.showMemberList')}
              description={t('tenant.showMemberListDescription')}
            />
            <ToggleSwitch
              checked={visibilityForm.allow_join_requests}
              onChange={(v) => setVisibilityForm((prev) => ({ ...prev, allow_join_requests: v }))}
              label={t('tenant.allowJoinRequests')}
              description={t('tenant.allowJoinRequestsDescription')}
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveVisibility} disabled={visibilitySubmitting}>
            {visibilitySubmitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>

      {/* ================================================================= */}
      {/* Card 3: Localization */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('tenant.localization')}</CardTitle>
              <CardDescription>{t('tenant.localizationDescription')}</CardDescription>
            </div>
            {localizationSaved && (
              <Badge variant="success" size="sm">{t('tenant.saved')}</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('tenant.defaultTimezone')}
            </label>
            <Select
              value={localizationForm.default_timezone}
              onChange={(e) => setLocalizationForm((prev) => ({ ...prev, default_timezone: e.target.value })) } className="w-full">
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, ' ')}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('tenant.defaultLocale')}
            </label>
            <Select
              value={localizationForm.default_locale}
              onChange={(e) => setLocalizationForm((prev) => ({ ...prev, default_locale: e.target.value })) } className="w-full">
              {LOCALE_OPTIONS.map((loc) => (
                <option key={loc.value} value={loc.value}>
                  {loc.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Input
              label={t('tenant.dataRetentionDays')}
              type="number"
              value={String(localizationForm.data_retention_days)}
              onChange={(e) =>
                setLocalizationForm((prev) => ({
                  ...prev,
                  data_retention_days: parseInt(e.target.value, 10) || 0,
                }))
              }
              placeholder="365"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('tenant.dataRetentionDaysHint')}
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveLocalization} disabled={localizationSubmitting}>
            {localizationSubmitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>

      {/* ================================================================= */}
      {/* Card 4: Feature Toggles */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('tenant.featureToggles')}</CardTitle>
              <CardDescription>{t('tenant.featureTogglesDescription')}</CardDescription>
            </div>
            {featuresSaved && (
              <Badge variant="success" size="sm">{t('tenant.saved')}</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ToggleSwitch
              checked={featuresForm.enable_api_access}
              onChange={(v) => setFeaturesForm((prev) => ({ ...prev, enable_api_access: v }))}
              label={t('tenant.enableApiAccess')}
              description={t('tenant.enableApiAccessDescription')}
            />
            <ToggleSwitch
              checked={featuresForm.enable_webhooks}
              onChange={(v) => setFeaturesForm((prev) => ({ ...prev, enable_webhooks: v }))}
              label={t('tenant.enableWebhooks')}
              description={t('tenant.enableWebhooksDescription')}
            />
            <ToggleSwitch
              checked={featuresForm.enable_integrations}
              onChange={(v) => setFeaturesForm((prev) => ({ ...prev, enable_integrations: v }))}
              label={t('tenant.enableIntegrations')}
              description={t('tenant.enableIntegrationsDescription')}
            />
            <ToggleSwitch
              checked={featuresForm.enable_audit_logging}
              onChange={(v) => setFeaturesForm((prev) => ({ ...prev, enable_audit_logging: v }))}
              label={t('tenant.enableAuditLogging')}
              description={t('tenant.enableAuditLoggingDescription')}
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveFeatures} disabled={featuresSubmitting}>
            {featuresSubmitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
});

export default TenantGeneralSettingsPage;
